import { useState, useEffect, useCallback } from 'react';
import { Button } from '../ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../ui/card';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Badge } from '../ui/badge';
import { Switch } from '../ui/switch';
import { Textarea } from '../ui/textarea';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '../ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../ui/select';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from '../ui/alert-dialog';
import { 
  Plus, 
  Loader2, 
  BookOpen,
  Edit,
  Trash2,
  Users,
  GraduationCap,
  DollarSign,
  Briefcase,
  Target,
  BarChart3,
  Code,
  Lightbulb,
  Rocket,
  Award
} from 'lucide-react';
import { toast } from 'sonner';
import { Link } from 'react-router-dom';

// Available icons for course modules
const AVAILABLE_ICONS = [
  { name: 'BookOpen', icon: BookOpen, label: 'Book' },
  { name: 'GraduationCap', icon: GraduationCap, label: 'Graduation' },
  { name: 'DollarSign', icon: DollarSign, label: 'Finance' },
  { name: 'Briefcase', icon: Briefcase, label: 'Business' },
  { name: 'Target', icon: Target, label: 'Target' },
  { name: 'BarChart3', icon: BarChart3, label: 'Analytics' },
  { name: 'Code', icon: Code, label: 'Code' },
  { name: 'Lightbulb', icon: Lightbulb, label: 'Ideas' },
  { name: 'Rocket', icon: Rocket, label: 'Rocket' },
  { name: 'Award', icon: Award, label: 'Award' },
  { name: 'Users', icon: Users, label: 'Team' },
];

// Available colors
const AVAILABLE_COLORS = [
  { name: 'blue', class: 'bg-blue-500', label: 'Blue' },
  { name: 'green', class: 'bg-green-500', label: 'Green' },
  { name: 'purple', class: 'bg-purple-500', label: 'Purple' },
  { name: 'orange', class: 'bg-orange-500', label: 'Orange' },
  { name: 'red', class: 'bg-red-500', label: 'Red' },
  { name: 'cyan', class: 'bg-cyan-500', label: 'Cyan' },
  { name: 'emerald', class: 'bg-emerald-500', label: 'Emerald' },
  { name: 'amber', class: 'bg-amber-500', label: 'Amber' },
  { name: 'pink', class: 'bg-pink-500', label: 'Pink' },
  { name: 'indigo', class: 'bg-indigo-500', label: 'Indigo' },
];

const getIconComponent = (iconName) => {
  const found = AVAILABLE_ICONS.find(i => i.name === iconName);
  return found ? found.icon : BookOpen;
};

const getColorClass = (colorName) => {
  const found = AVAILABLE_COLORS.find(c => c.name === colorName);
  return found ? found.class : 'bg-blue-500';
};

/**
 * CourseModulesManager - Admin panel to create and manage dynamic course modules
 */
const CourseModulesManager = ({ api }) => {
  const [modules, setModules] = useState([]);
  const [loading, setLoading] = useState(true);
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [selectedModule, setSelectedModule] = useState(null);
  const [saving, setSaving] = useState(false);
  
  // Form state
  const [formData, setFormData] = useState({
    name: '',
    slug: '',
    description: '',
    icon: 'BookOpen',
    color: 'blue',
    is_active: true
  });

  const fetchModules = useCallback(async () => {
    try {
      setLoading(true);
      const response = await api.get('/course-modules?include_inactive=true');
      setModules(response.data.modules || []);
    } catch (error) {
      console.error('Failed to fetch modules:', error);
      toast.error('Failed to load course modules');
    } finally {
      setLoading(false);
    }
  }, [api]);

  useEffect(() => {
    fetchModules();
  }, [fetchModules]);

  const generateSlug = (name) => {
    return name
      .toLowerCase()
      .replace(/[^a-z0-9\s-]/g, '')
      .replace(/\s+/g, '-')
      .replace(/-+/g, '-')
      .trim();
  };

  const handleNameChange = (e) => {
    const name = e.target.value;
    setFormData(prev => ({
      ...prev,
      name,
      slug: generateSlug(name)
    }));
  };

  const resetForm = () => {
    setFormData({
      name: '',
      slug: '',
      description: '',
      icon: 'BookOpen',
      color: 'blue',
      is_active: true
    });
  };

  const handleCreate = async () => {
    if (!formData.name.trim()) {
      toast.error('Please enter a module name');
      return;
    }
    if (!formData.slug.trim()) {
      toast.error('Please enter a valid slug');
      return;
    }

    try {
      setSaving(true);
      await api.post('/course-modules', formData);
      toast.success(`Course module "${formData.name}" created successfully`);
      setCreateDialogOpen(false);
      resetForm();
      fetchModules();
    } catch (error) {
      console.error('Failed to create module:', error);
      toast.error(error.response?.data?.detail || 'Failed to create course module');
    } finally {
      setSaving(false);
    }
  };

  const openEditDialog = (module) => {
    setSelectedModule(module);
    setFormData({
      name: module.name,
      slug: module.slug,
      description: module.description || '',
      icon: module.icon,
      color: module.color,
      is_active: module.is_active
    });
    setEditDialogOpen(true);
  };

  const handleUpdate = async () => {
    if (!selectedModule) return;

    try {
      setSaving(true);
      await api.put(`/course-modules/${selectedModule.id}`, {
        name: formData.name,
        description: formData.description,
        icon: formData.icon,
        color: formData.color,
        is_active: formData.is_active
      });
      toast.success(`Course module "${formData.name}" updated successfully`);
      setEditDialogOpen(false);
      setSelectedModule(null);
      resetForm();
      fetchModules();
    } catch (error) {
      console.error('Failed to update module:', error);
      toast.error(error.response?.data?.detail || 'Failed to update course module');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (module) => {
    try {
      await api.delete(`/course-modules/${module.id}`);
      toast.success(`Course module "${module.name}" has been deactivated`);
      fetchModules();
    } catch (error) {
      console.error('Failed to delete module:', error);
      toast.error(error.response?.data?.detail || 'Failed to deactivate module');
    }
  };

  const IconComponent = getIconComponent(formData.icon);

  return (
    <Card>
      <CardHeader>
        <div className="flex items-center justify-between">
          <div>
            <CardTitle className="flex items-center gap-2">
              <GraduationCap className="h-5 w-5" />
              Course Modules Manager
            </CardTitle>
            <CardDescription>
              Create and manage custom training modules. Each module gets its own course library and builder.
            </CardDescription>
          </div>
          <Dialog open={createDialogOpen} onOpenChange={setCreateDialogOpen}>
            <DialogTrigger asChild>
              <Button data-testid="create-module-btn">
                <Plus className="h-4 w-4 mr-2" />
                Create Module
              </Button>
            </DialogTrigger>
            <DialogContent className="max-w-md">
              <DialogHeader>
                <DialogTitle>Create New Course Module</DialogTitle>
                <DialogDescription>
                  Create a new training module with its own courses, assignments, and progress tracking.
                </DialogDescription>
              </DialogHeader>
              <div className="space-y-4 py-4">
                <div className="space-y-2">
                  <Label htmlFor="module-name">Module Name *</Label>
                  <Input
                    id="module-name"
                    placeholder="e.g., Sales Training"
                    value={formData.name}
                    onChange={handleNameChange}
                    data-testid="module-name-input"
                  />
                </div>
                
                <div className="space-y-2">
                  <Label htmlFor="module-slug">URL Slug</Label>
                  <Input
                    id="module-slug"
                    placeholder="e.g., sales-training"
                    value={formData.slug}
                    onChange={(e) => setFormData({ ...formData, slug: e.target.value })}
                    data-testid="module-slug-input"
                  />
                  <p className="text-xs text-muted-foreground">
                    Used in URLs: /sales-training/courses
                  </p>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="module-description">Description</Label>
                  <Textarea
                    id="module-description"
                    placeholder="Brief description of this training module..."
                    value={formData.description}
                    onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                    rows={2}
                  />
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Icon</Label>
                    <Select value={formData.icon} onValueChange={(v) => setFormData({ ...formData, icon: v })}>
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {AVAILABLE_ICONS.map((icon) => {
                          const Icon = icon.icon;
                          return (
                            <SelectItem key={icon.name} value={icon.name}>
                              <div className="flex items-center gap-2">
                                <Icon className="h-4 w-4" />
                                {icon.label}
                              </div>
                            </SelectItem>
                          );
                        })}
                      </SelectContent>
                    </Select>
                  </div>

                  <div className="space-y-2">
                    <Label>Color</Label>
                    <Select value={formData.color} onValueChange={(v) => setFormData({ ...formData, color: v })}>
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {AVAILABLE_COLORS.map((color) => (
                          <SelectItem key={color.name} value={color.name}>
                            <div className="flex items-center gap-2">
                              <div className={`h-4 w-4 rounded ${color.class}`} />
                              {color.label}
                            </div>
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>

                {/* Preview */}
                <div className="pt-2 border-t">
                  <Label className="text-xs text-muted-foreground">Preview</Label>
                  <div className="flex items-center gap-3 mt-2 p-3 rounded-lg border bg-muted/30">
                    <div className={`p-2 rounded-lg ${getColorClass(formData.color)}/10`}>
                      <IconComponent className={`h-5 w-5 text-${formData.color}-500`} />
                    </div>
                    <div>
                      <p className="font-medium">{formData.name || 'Module Name'}</p>
                      <p className="text-xs text-muted-foreground">{formData.description || 'Description...'}</p>
                    </div>
                  </div>
                </div>
              </div>
              <DialogFooter>
                <Button variant="outline" onClick={() => { setCreateDialogOpen(false); resetForm(); }}>
                  Cancel
                </Button>
                <Button onClick={handleCreate} disabled={saving} data-testid="save-module-btn">
                  {saving ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Plus className="h-4 w-4 mr-2" />}
                  Create Module
                </Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        </div>
      </CardHeader>
      <CardContent>
        {loading ? (
          <div className="flex items-center justify-center py-8">
            <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
          </div>
        ) : modules.length === 0 ? (
          <div className="text-center py-8 text-muted-foreground">
            <GraduationCap className="h-12 w-12 mx-auto mb-3 opacity-50" />
            <p>No custom course modules yet</p>
            <p className="text-sm">Create your first module to get started</p>
          </div>
        ) : (
          <div className="space-y-3">
            {modules.map((module) => {
              const ModuleIcon = getIconComponent(module.icon);
              return (
                <div
                  key={module.id}
                  className={`flex items-center justify-between p-4 rounded-lg border ${
                    module.is_active ? '' : 'opacity-60 bg-muted/30'
                  }`}
                >
                  <div className="flex items-center gap-4">
                    <div className={`p-2.5 rounded-lg ${getColorClass(module.color)}/10`}>
                      <ModuleIcon className={`h-5 w-5 text-${module.color}-500`} />
                    </div>
                    <div>
                      <div className="flex items-center gap-2">
                        <p className="font-medium">{module.name}</p>
                        {!module.is_active && (
                          <Badge variant="outline" className="text-xs">Inactive</Badge>
                        )}
                      </div>
                      <p className="text-sm text-muted-foreground">
                        {module.description || `/${module.slug}`}
                      </p>
                      <div className="flex items-center gap-3 mt-1 text-xs text-muted-foreground">
                        <span className="flex items-center gap-1">
                          <BookOpen className="h-3 w-3" />
                          {module.course_count || 0} courses
                        </span>
                        <span className="flex items-center gap-1">
                          <Users className="h-3 w-3" />
                          {module.user_count || 0} users
                        </span>
                      </div>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    {module.is_active && (
                      <Button asChild variant="outline" size="sm" data-testid={`open-builder-${module.slug}`}>
                        <Link to={`/admin/module-builder/${module.slug}`}>Open Builder</Link>
                      </Button>
                    )}
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => openEditDialog(module)}
                      data-testid={`edit-module-${module.slug}`}
                    >
                      <Edit className="h-4 w-4" />
                    </Button>
                    {module.is_active && (
                      <AlertDialog>
                        <AlertDialogTrigger asChild>
                          <Button variant="ghost" size="sm" className="text-destructive hover:text-destructive" data-testid={`delete-module-${module.slug}`}>
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </AlertDialogTrigger>
                        <AlertDialogContent>
                          <AlertDialogHeader>
                            <AlertDialogTitle>Deactivate Module?</AlertDialogTitle>
                            <AlertDialogDescription>
                              This will hide &quot;{module.name}&quot; from the navigation but preserve all data. 
                              You can reactivate it later.
                            </AlertDialogDescription>
                          </AlertDialogHeader>
                          <AlertDialogFooter>
                            <AlertDialogCancel>Cancel</AlertDialogCancel>
                            <AlertDialogAction
                              onClick={() => handleDelete(module)}
                              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
                            >
                              Deactivate
                            </AlertDialogAction>
                          </AlertDialogFooter>
                        </AlertDialogContent>
                      </AlertDialog>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        )}

        {/* Edit Dialog */}
        <Dialog open={editDialogOpen} onOpenChange={setEditDialogOpen}>
          <DialogContent className="max-w-md">
            <DialogHeader>
              <DialogTitle>Edit Course Module</DialogTitle>
              <DialogDescription>
                Update the module settings. The URL slug cannot be changed.
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-4 py-4">
              <div className="space-y-2">
                <Label htmlFor="edit-module-name">Module Name *</Label>
                <Input
                  id="edit-module-name"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                />
              </div>

              <div className="space-y-2">
                <Label>URL Slug (read-only)</Label>
                <Input value={formData.slug} disabled className="bg-muted" />
              </div>

              <div className="space-y-2">
                <Label htmlFor="edit-module-description">Description</Label>
                <Textarea
                  id="edit-module-description"
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  rows={2}
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Icon</Label>
                  <Select value={formData.icon} onValueChange={(v) => setFormData({ ...formData, icon: v })}>
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {AVAILABLE_ICONS.map((icon) => {
                        const Icon = icon.icon;
                        return (
                          <SelectItem key={icon.name} value={icon.name}>
                            <div className="flex items-center gap-2">
                              <Icon className="h-4 w-4" />
                              {icon.label}
                            </div>
                          </SelectItem>
                        );
                      })}
                    </SelectContent>
                  </Select>
                </div>

                <div className="space-y-2">
                  <Label>Color</Label>
                  <Select value={formData.color} onValueChange={(v) => setFormData({ ...formData, color: v })}>
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {AVAILABLE_COLORS.map((color) => (
                        <SelectItem key={color.name} value={color.name}>
                          <div className="flex items-center gap-2">
                            <div className={`h-4 w-4 rounded ${color.class}`} />
                            {color.label}
                          </div>
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>

              <div className="flex items-center justify-between pt-2">
                <Label htmlFor="module-active">Module Active</Label>
                <Switch
                  id="module-active"
                  checked={formData.is_active}
                  onCheckedChange={(checked) => setFormData({ ...formData, is_active: checked })}
                />
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => { setEditDialogOpen(false); setSelectedModule(null); resetForm(); }}>
                Cancel
              </Button>
              <Button onClick={handleUpdate} disabled={saving}>
                {saving ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}
                Save Changes
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </CardContent>
    </Card>
  );
};

export default CourseModulesManager;
